/* dropbox.jsx — "Send to Dropbox" build-delivery feature (Viper handoff Goose-14).
   A Dropbox-blue card on the job-detail page that pushes a packaged build's REAL
   delivery files to Dropbox (server-side, via a Dropbox App), then lists the
   shareable links. Same global-component pattern as the other screens; registers
   window.DropboxAPI + window.SendToDropbox; jobdetail.jsx mounts the card.

   The server route (POST /api/dropbox/deliver) is being scoped + built by 🔥
   Phoenix (DBX-01) — the Dropbox App secrets live server-side, never the browser.
   Until it lands, deliver() degrades honestly ("being wired"); when it lands, the
   component + call site do NOT change (Viper's swap seam). */

(function () {
  const fail = (status, code) => { const e = new Error(code || String(status)); e.status = status; e.code = code; return e; };

  window.DropboxAPI = {
    // Optimistic: the button is enabled; a 412 from the route surfaces "no Dropbox
    // App configured" (the FE can't read server config). The server is the source of truth.
    isConfigured() { return true; },

    // POST /api/dropbox/deliver { jobId } → { folder, links:[{name,url,bytes}] }.
    // The route uploads the build's REAL packaged outputs + returns shareable links.
    // TODO (Iceman, HOT): fold into GooseAPI once DBX-01's route lands — this raw
    // fetch is the sanctioned stopgap (RBAC-07 pattern).
    async deliver(job) {
      const r = await fetch('/api/dropbox/deliver', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId: job && job.id }),
      }).catch(() => null);
      if (!r) throw fail(0, 'dropbox_not_wired');                       // network / route absent
      if (r.status === 404 || r.status === 501) throw fail(r.status, 'dropbox_not_wired');
      let data = {}; try { data = await r.json(); } catch (_) {}
      if (!r.ok) throw fail(r.status, data.code || 'dropbox_error');
      return data;                                                      // { folder, links, cached? }
    },

    // DBX-05: GET /api/dropbox/delivery/:jobId → the LAST delivery from disk (no Dropbox
    // round-trip). null when nothing was ever delivered (404) or the route isn't there.
    async existing(job) {
      const r = await fetch('/api/dropbox/delivery/' + encodeURIComponent(job && job.id), {
        credentials: 'same-origin',
      }).catch(() => null);
      if (!r || !r.ok) return null;
      let data = null; try { data = await r.json(); } catch (_) {}
      return data && Array.isArray(data.links) ? data : null;
    },
  };
})();

/* ---------- the Dropbox glyph (official mark) ---------- */
function DropboxGlyph({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path fill="currentColor" d="M6 1.807L0 5.629l6 3.822 6-3.822zm12 0l-6 3.822 6 3.822 6-3.822zM0 13.274l6 3.822 6-3.822-6-3.821zm18-3.821l-6 3.821 6 3.822 6-3.822zM6 18.371l6 3.822 6-3.822-6-3.822z" />
    </svg>
  );
}

const fmtBytes = (b) => (b >= 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.round(b / 1024) + ' KB');

/* ---------- Send to Dropbox (job-detail delivery card) ---------- */
function SendToDropbox({ job }) {
  const [state, setState] = React.useState('idle');     // idle | sending | done | error
  const [result, setResult] = React.useState(null);      // {folder, links}
  const [err, setErr] = React.useState(null);
  const [copied, setCopied] = React.useState(null);      // url just copied
  const [copiedAll, setCopiedAll] = React.useState(false);
  const configured = window.DropboxAPI.isConfigured();

  // DBX-05: on open, show the links that already exist — the persisted last delivery —
  // instead of making the user re-send (and re-upload) to see them again.
  React.useEffect(() => {
    let alive = true;
    window.DropboxAPI.existing(job).then((r) => { if (alive && r) { setResult(r); setState('done'); } });
    return () => { alive = false; };
  }, [job && job.id]);

  const send = async () => {
    setState('sending'); setErr(null);
    try {
      const r = await window.DropboxAPI.deliver(job);
      setResult(r); setState('done');
    } catch (e) {
      setErr(
        e.code === 'dropbox_rate_limited' ? 'Dropbox rate-limited the upload — wait a moment and re-send.'
        : e.code === 'dropbox_unconfigured' ? 'No Dropbox App is configured on the server yet.'
        : e.code === 'dropbox_not_wired' ? 'Dropbox delivery isn’t wired into the server yet — the route is being built (DBX-01). The card lights up the moment it lands.'
        : 'Couldn’t reach Dropbox. Try again.',
      );
      setState('error');
    }
  };

  const copy = (url) => {
    try { navigator.clipboard.writeText(url); } catch (e) {}
    setCopied(url); setTimeout(() => setCopied((c) => (c === url ? null : c)), 1400);
  };
  const copyAll = () => {
    if (!result) return;
    const text = result.links.map((l) => l.name + ': ' + l.url).join('\n');
    try { navigator.clipboard.writeText(text); } catch (e) {}
    setCopiedAll(true); setTimeout(() => setCopiedAll(false), 1400);
  };

  const sending = state === 'sending';

  return (
    <div className="card card-pad dbx-card">
      <div className="dbx-head">
        <div className="dbx-title">
          <span className="dbx-mark"><DropboxGlyph size={18} /></span>
          <div>
            <div className="dbx-t">Deliver to Dropbox</div>
            <div className="dbx-sub">Push the packaged build to the client’s Dropbox and get shareable links.</div>
          </div>
        </div>
        <button className="dbx-btn" onClick={send} disabled={sending || !configured}
          title={configured ? 'Upload the delivery files to Dropbox' : 'No Dropbox App configured'}>
          {sending
            ? <React.Fragment><span className="spinner dbx-spin"></span>Sending…</React.Fragment>
            : <React.Fragment><DropboxGlyph size={16} />{state === 'done' ? 'Re-send to Dropbox' : 'Send to Dropbox'}</React.Fragment>}
        </button>
      </div>

      {state === 'error' && (
        <div className="dbx-err"><I.warn size={14} /> {err} <button className="dbx-retry" onClick={send}>Retry</button></div>
      )}

      {state === 'done' && result && (
        <div className="dbx-links">
          <div className="dbx-links-head">
            <span><I.check size={13} style={{ color: 'var(--pass)', verticalAlign: '-2px', marginRight: 5 }} />{result.links.length} files {result.cached ? 'already delivered' : 'delivered'} to <span className="mono">{result.folder}</span>{result.cached ? <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>· links current, nothing re-uploaded</span> : null}</span>
            <button className="dbx-copyall" onClick={copyAll}>{copiedAll ? 'Copied!' : 'Copy all'}</button>
          </div>
          {result.links.map((l) => (
            <div key={l.url} className="dbx-link">
              <span className="dbx-file-ico"><I.doc size={15} /></span>
              <div className="dbx-link-meta">
                <div className="dbx-link-name">{l.name} <span className="dbx-link-size">{fmtBytes(l.bytes)}</span></div>
                <a className="dbx-link-url mono" href={l.url} target="_blank" rel="noopener">{l.url}</a>
              </div>
              <button className="dbx-copy" onClick={() => copy(l.url)} title="Copy link">
                {copied === l.url ? <I.check size={14} /> : <I.copy size={14} />}
              </button>
              <a className="dbx-open" href={l.url} target="_blank" rel="noopener" title="Open in Dropbox"><I.arrow size={14} /></a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

Object.assign(window, { SendToDropbox, DropboxGlyph });
